import {
  AlignJustifyIcon,
  DropdownOption,
  EyeIcon,
  IconSizeEnum,
  PlusIcon,
  ServicesCodeIcon,
  SquareIcon,
  TextPictureIcon,
} from '@acceso/design-system';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { TPostClipLogo } from '@/features/clips/types';
import { usePostLogos } from '@/features/logos/api/post-logos';
import { TLogo, TLogosForm } from '@/features/logos/types';
import {
  TPatchSegmentLogoBody,
  usePatchSegmentLogo,
} from '@/features/segments/api/patch-segment-logo';
import { usePostSegmentLogo } from '@/features/segments/api/post-segment-logo';
import { TPostSegmentLogo } from '@/features/segments/types';
import { useDisclosureWithData } from '@/hooks/useDisclosureWithData';
import { FormTypeEnum } from '@/types';
import { PresenceTypeEnum } from '@/types/presences';
import { convertISOToSeconds } from '@/utils/hh-mm-ss-into-number';
import { LOGO_FORM_DEFAULT_VALUES } from 'src/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/constants';
import {
  GROUP_OPTION_VALUES,
  TBroadcastScheduleProgramBodySegmentToolkitLogotiposForm,
} from 'src/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/types';

export function useSegmentPresences() {
  const {
    logoData,
    drawerRect,
    activeSegment,
    formBox,
    playerVideoCurrentTime,
    getDateTimeCurrentSegment,
    setLogoData,
    setFormBox,
    setActiveSegment,
    setVisibleBox,
    drawnFormReset,
    setSegmentItems,
  } = useBroadcastScheduleProgram();
  const { t } = useTranslation();
  const postSegmentLogo = usePostSegmentLogo();
  const patchSegmentLogo = usePatchSegmentLogo();
  const form = useForm<TBroadcastScheduleProgramBodySegmentToolkitLogotiposForm>({
    mode: 'all',
    defaultValues: LOGO_FORM_DEFAULT_VALUES,
  });

  const drawerLogo = useDisclosureWithData();
  const { mutateAsync: createLogos, isPending: isCreatingLogos } = usePostLogos();

  const buildGroupOptions = useCallback(
    (groupType: PresenceTypeEnum): DropdownOption[] => {
      const baseOptions = [
        {
          label: t('Presencia'),
          value: GROUP_OPTION_VALUES.Presence,
          icon: <TextPictureIcon size={IconSizeEnum.XS} />,
          className: `!justify-start`,
        },
        {
          label: t('Visibilidad'),
          value: GROUP_OPTION_VALUES.Visibility,
          icon: <EyeIcon size={IconSizeEnum.XS} />,
          className: `!justify-start`,
        },
        {
          label: t('Fiabilidad'),
          value: GROUP_OPTION_VALUES.Reliability,
          icon: <ServicesCodeIcon size={IconSizeEnum.XS} />,
          className: `!justify-start`,
        },
        {
          label: t('(Ninguno)'),
          value: GROUP_OPTION_VALUES.None,
          icon: <AlignJustifyIcon size={IconSizeEnum.XS} />,
          className: `!justify-start`,
        },
      ];

      if (groupType === PresenceTypeEnum.PEOPLE) {
        return baseOptions;
      }

      const supportOption = {
        label: t('Soporte'),
        value: GROUP_OPTION_VALUES.Support,
        icon: <SquareIcon size={IconSizeEnum.XS} />,
        className: `!justify-start`,
      };

      return [...baseOptions.slice(0, 1), supportOption, ...baseOptions.slice(1)];
    },
    [t]
  );

  const renderAdditionalButtons = () => {
    return [
      {
        label: t('Crear nuevo logotipo'),
        icon: PlusIcon,
        onClick: () =>
          drawerLogo.open({
            form: null,
            type: FormTypeEnum.CREATE,
          }),
      },
    ];
  };

  const handleSubmitLogo = useCallback(
    async (data: TLogosForm): Promise<TLogo> => {
      try {
        const result = await createLogos(data);
        drawerLogo.close();
        return result;
      } catch (error) {
        console.error('Error during mutation', error);
        throw error;
      }
    },
    [createLogos]
  );

  const handleSubmit = async (data: TBroadcastScheduleProgramBodySegmentToolkitLogotiposForm) => {
    const start_datetime =
      data.start_datetime ?? getDateTimeCurrentSegment(playerVideoCurrentTime) ?? null;
    const formatedData: TPostSegmentLogo = {
      ...data,
      start_datetime,
      end_datetime: data.end_datetime ?? start_datetime,
      parameters: {
        ...data.parameters,
        bbox: data.parameters.bbox || [0.045, 0.075, 0.96, 0.925], // Full screen
        segment_id: activeSegment?.id || '',
      },
      segment_id: activeSegment?.id || '',
      entity_score: Number(data.entity_score) / 100,
    };
    if (logoData?.id) {
      await handleUpdate(formatedData);
    } else {
      await handleCreate(formatedData);
    }
  };

  const handleCreate = async (formatedData: TPostSegmentLogo) => {
    try {
      const item = await postSegmentLogo.mutateAsync(formatedData);
      setActiveSegment(prev => {
        if (prev) {
          return {
            ...prev,
            segment_logos: [...(prev.segment_logos || []), item],
          };
        }
        return prev;
      });
      setSegmentItems(prev =>
        prev.map(segment => {
          if (segment.id === formatedData?.segment_id) {
            return {
              ...segment,
              segment_logos: [...(segment.segment_logos || []), item],
            };
          }
          return segment;
        })
      );
      drawnFormReset();
    } catch (error) {
      console.error('Error al crear el logo:', error);
    }
  };

  const handleUpdate = async (formatedData: TPostSegmentLogo | TPostClipLogo) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id, silently, segment_id, ...restData } = formatedData;
    delete formatedData.id;
    const xhrParams: TPatchSegmentLogoBody = {
      id: id || logoData?.id || '',
      silently,
      body: restData,
    };

    try {
      const item = await patchSegmentLogo.mutateAsync(xhrParams);
      setActiveSegment(prev => {
        if (prev) {
          return {
            ...prev,
            segment_logos: prev.segment_logos?.map(logo => {
              if (logo.id === item.id) {
                return item;
              }
              return logo;
            }),
          };
        }
        return prev;
      });
      setSegmentItems(prev =>
        prev.map(segment => {
          if (segment.id === formatedData?.segment_id) {
            return {
              ...segment,
              segment_logos: (segment.segment_logos || []).map(ocr => {
                if (ocr.id === item.id) {
                  return item;
                }
                return ocr;
              }),
            };
          }
          return segment;
        })
      );
      drawnFormReset();
    } catch (error) {
      console.error('Error al actualizar el logo:', error);
    }
  };

  const handleClickExit = useCallback(() => {
    form.reset(LOGO_FORM_DEFAULT_VALUES);
    drawnFormReset();
  }, [setFormBox, setLogoData]);

  useEffect(() => {
    if (drawerRect && formBox === 'logo') {
      form.setValue('parameters.bbox', drawerRect);
    }
  }, [drawerRect]);

  useEffect(() => {
    if (formBox !== 'logo') return;

    if (logoData?.id) {
      form.setValue('logo_id', logoData.logo_id);
      form.setValue('parameters.bbox', logoData.parameters.bbox);
      form.setValue('start_datetime', new Date(logoData.start_datetime));
      form.setValue('end_datetime', new Date(logoData.end_datetime || logoData.start_datetime));
      form.setValue('entity_score', logoData.entity_score * 100);
      form.setValue('review_state', logoData.review_state);
      form.setValue('parameters.visibility', logoData.parameters.visibility);
      form.setValue('parameters.support_id', logoData.parameters.support_id);

      if (!activeSegment || !logoData.start_datetime) return;

      const seconds = convertISOToSeconds(
        logoData.start_datetime,
        new Date(activeSegment.start_datetime)
      );

      setVisibleBox({
        bbox: logoData?.parameters.bbox || [0, 0, 0, 0],
        frame: seconds,
        entity_label: logoData?.entity_label || '',
      });
    }
  }, [logoData]);

  return {
    form,
    drawerLogo,
    isCreatingLogos,
    buildGroupOptions,
    renderAdditionalButtons,
    handleSubmitLogo,
    handleSubmit,
    handleUpdate,
    handleClickExit,
  };
}
